import { concat, fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { repeat } from '@nullvoxpopuli/ember-composable-helpers';
import type AbilitiesService from 'ember-can/services/abilities';
import MarkdownToHtml from 'ember-cli-showdown/components/markdown-to-html';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import { pluralize } from 'ember-inflector';
import add from 'ember-math-helpers/helpers/add';
import sub from 'ember-math-helpers/helpers/sub';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq, gt, notEq, or } from 'ember-truth-helpers';
import { sourceToAbilityMap } from 'garaje/abilities/entry-approval';
import type { GetCanReviewOrReadBlocklistMatchModel } from 'garaje/abilities/security';
import Card from 'garaje/components/ui/card';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import UiButton from 'garaje/components/ui-button';
import type { ScreenerResultDetailsField, ScreenerResultDetailsMatch } from 'garaje/models/screener-result';
import type ScreenerResult from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import { TrackedWeakMap } from 'tracked-built-ins';

interface UnifiedScreenersModalSignature {
  Args: {
    model: GuestWithVisitApproval;
    focusedResult?: ScreenerResult;
    onClose: () => void;
  };
  Blocks: {
    actions: [ScreenerResult, onApprove: () => void];
  };
}

export default class UnifiedScreenersModal extends Component<UnifiedScreenersModalSignature> {
  @service declare abilities: AbilitiesService;

  fieldVisibilityPerMatch = new TrackedWeakMap<ScreenerResultDetailsMatch, boolean>();

  @tracked selectedIndex = 0;

  get selectedResult(): ScreenerResult {
    return this.visibleResults[this.selectedIndex]!;
  }

  get isLast(): boolean {
    return this.selectedIndex === this.visibleResults.length - 1;
  }

  get #permissionsModel(): GetCanReviewOrReadBlocklistMatchModel {
    return {
      context: 'location',
      parent: this.args.model,
      visitApproval: this.args.model.visitApproval,
    };
  }

  get isVisualCompliance(): boolean {
    return this.selectedResult?.isVisualCompliance() ?? false;
  }

  get visibleResults(): ScreenerResult[] {
    // look at all screeners not just in review to avoid this getter from recomputing
    // and messing with the selected index while navigating
    const visibleResults = this.args.model.visitApproval.screenerResults.filter((result) => {
      const source = sourceToAbilityMap[result.screenerSource] || result.screenerSource;
      const canView = this.abilities.can(`view ${source}`, this.#permissionsModel);
      const canReview = this.abilities.can(`review ${source}`, this.#permissionsModel);

      return result.details && (canView || canReview);
    });

    const { focusedResult } = this.args;

    // bring result into focus if the modal was opened from the scope of a specific result
    const index = visibleResults.findIndex((result) => result.screenerSource === focusedResult?.screenerSource);
    if (index !== -1) {
      const [focused] = visibleResults.splice(index, 1);
      visibleResults.unshift(focused!);
    }

    return visibleResults;
  }

  get visibleReviewableResults(): ScreenerResult[] {
    return this.visibleResults.filter((result) => result.isReview);
  }

  @action
  getFieldsForMatch(match: ScreenerResultDetailsMatch): ScreenerResultDetailsField[] {
    const max = match['visible-fields-count'];

    // eslint-disable-next-line ember/use-ember-get-and-set
    const showFields = this.fieldVisibilityPerMatch.get(match) ?? false;
    const lessThanMax = max ? match.fields.length < max : true;

    if (showFields || lessThanMax) return match.fields;
    return match.fields.slice(0, max!) ?? [];
  }

  @action
  toggleVisibility(match: ScreenerResultDetailsMatch): void {
    // eslint-disable-next-line ember/use-ember-get-and-set
    this.fieldVisibilityPerMatch.set(match, !this.fieldVisibilityPerMatch.get(match));
  }

  @action
  getVisibilityPerMatch(match: ScreenerResultDetailsMatch): boolean | undefined {
    // eslint-disable-next-line ember/use-ember-get-and-set
    return this.fieldVisibilityPerMatch.get(match);
  }

  @action
  prev(): void {
    this.selectedIndex -= 1;
  }

  @action
  next(): void {
    this.selectedIndex += 1;
  }

  @action
  didApprove(): void {
    // if there are no more reviewable results left, close the modal
    if (this.visibleReviewableResults.length === 0) {
      this.args.onClose();
      return;
    }

    if (this.isLast) {
      // go to the first reviewable result if we're at the end of the carousel
      this.selectedIndex = this.visibleResults.indexOf(this.visibleReviewableResults[0]!);
    } else {
      // go to the next result
      this.next();
    }
  }

  <template>
    <ModalDialog
      @clickOutsideToClose={{true}}
      @translucentOverlay={{true}}
      @containerClass="!max-w-[1013px]"
      @onClose={{@onClose}}
    >
      <FocusTrapper>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="unified-screeners-modal-title"
          class="flex"
          data-test-unified-screeners-modal={{this.selectedResult.id}}
          data-test-unified-screeners-modal-screener-name={{this.selectedResult.screenerSource}}
        >
          <section class="w-142 pt-10 pb-8 px-8 bg-carbon-5">
            <div class="flex">
              {{svgJar "logo-small-red" height="16" width="16"}}
              <div class="flex flex-col ml-2">
                <h4 class="mb-2">
                  Matched data references from Envoy
                </h4>
                {{#if (has-block "actions")}}
                  <p class="text-sm text-carbon-60">
                    Please review before taking any action.
                  </p>
                {{/if}}
              </div>
            </div>
            {{#if (has-block "actions")}}
              {{yield this.selectedResult this.didApprove to="actions"}}
            {{/if}}

            {{#if this.selectedResult.details.input.fields.length}}
              <Card class="mt-6 border-carbon-20" data-test-input-details>
                {{#each this.selectedResult.details.input.fields as |field|}}
                  <div class="flex flex-col" data-test-input-details-field>
                    <span class="text-sm !text-carbon-30 font-normal">{{field.name}}:</span>
                    <p class="text-base font-bold">{{if field.value field.value "--"}}</p>
                  </div>
                {{/each}}
              </Card>
            {{/if}}
          </section>

          <section class="!max-h-[715px] h-[715px] w-full flex flex-col" data-test-unified-screeners-modal-content>
            <header class="flex justify-between items-end mx-8 my-6 mb-2">
              <h4 id="unified-screeners-modal-title">Flagged Results</h4>
              <button
                data-test-trigger-close
                class="btn btn--small btn--outline btn--cancel"
                {{on "click" @onClose}}
                type="button"
              >
                <span aria-hidden="true">&times;</span>
                <span class="sr-only">close</span>
              </button>
            </header>

            {{#if this.selectedResult.details.matches}}
              <div class="overflow-y-auto">
                <Card class="mx-8 my-8" data-test-match-details>
                  <div class="flex items-center py-2">
                    {{#if (get this.selectedResult.details "logo-url")}}
                      <img
                        class="w-5 h-5 mr-2"
                        src={{get this.selectedResult.details "logo-url"}}
                        alt=""
                        role="presentation"
                      />
                    {{/if}}
                    <h3 class="text-lg">
                      {{this.selectedResult.details.title}}
                      <span class="text-papaya-50 font-normal">•
                        {{pluralize this.selectedResult.details.matches.length "match"}}
                      </span>
                    </h3>

                  </div>
                  <MarkdownToHtml
                    data-test-subtitle
                    class="text-sm"
                    @markdown={{this.selectedResult.details.subtitle}}
                  />

                  {{#each this.selectedResult.details.matches as |match index|}}
                    <table data-test-match={{index}} class="my-3">
                      <tr>
                        <th
                          class="rounded-tl-md w-28 h-9 text-xs text-left text-carbon-50 align-middle font-semibold bg-carbon-5 border border-carbon-10"
                        >
                          {{match.headers.name}}
                        </th>
                        <th
                          class="rounded-tr-md w-28 h-9 text-xs text-left text-carbon-50 align-middle font-semibold bg-carbon-5 border border-carbon-10"
                        >
                          <div class="flex justify-between items-center">
                            {{match.headers.value}}
                            {{#let (get match "alert-level") as |alertLevel|}}
                              {{#if alertLevel}}
                                <div
                                  class="flex items-center px-2 py-0.5 rounded-full
                                    {{if (eq alertLevel 1) 'bg-papaya-10' 'bg-red-10'}}"
                                >
                                  {{#each (repeat alertLevel) as |_ idx|}}
                                    <div
                                      class="rounded-full mr-1 w-[10px] h-[10px] {{if idx 'bg-red-50' 'bg-papaya-30'}}"
                                    >
                                    </div>
                                  {{/each}}
                                  <div
                                    class="ml-1 mt-0.5 tracking-wide
                                      {{if (eq alertLevel 1) 'text-papaya-50' 'text-red-60'}}"
                                    data-test-match-attr-alert-level
                                  >
                                    LEVEL
                                    {{alertLevel}}
                                  </div>
                                </div>
                              {{/if}}
                            {{/let}}
                          </div>
                        </th>
                      </tr>
                      {{#let (this.getFieldsForMatch match) as |fields|}}
                        {{#each fields as |field index|}}
                          <tr data-test-match-attr={{field.name}}>
                            <th
                              class="w-28 text-left align-top font-semibold py-2 border-x border-b border-carbon-10
                                {{if (eq index (sub match.fields.length 1)) 'rounded-bl-md'}}"
                              data-test-match-attr-label={{field.name}}
                            >
                              {{field.name}}
                            </th>
                            <td
                              class="text-left text-carbon-50 align-top py-2 border-r border-b border-carbon-10
                                {{if (eq index (sub match.fields.length 1)) 'rounded-br-md'}}"
                              data-test-field-value={{field.name}}
                            >
                              {{#if (eq field.type "image")}}
                                <img src={{field.value}} alt="" role="presentation" class="w-28 h-28 object-cover" />
                              {{else}}
                                {{field.value}}
                              {{/if}}
                            </td>
                          </tr>
                        {{/each}}
                        {{#let (notEq fields.length match.fields.length) as |notRenderingAllFields|}}
                          {{#if (or notRenderingAllFields (eq (this.getVisibilityPerMatch match) true))}}
                            <tr>
                              <td
                                class="w-28 text-left align-top font-semibold py-2 border-x border-b border-carbon-10 rounded-bl-md"
                              >
                              </td>
                              <td
                                class="w-28 align-top font-semibold py-2 border-x border-b border-carbon-10 rounded-br-md text-center"
                              >
                                <UiButton
                                  {{on "click" (fn this.toggleVisibility match)}}
                                  class="text-sm text-gem-60"
                                  type="button"
                                  data-test-toggle-fields-visibility
                                >
                                  {{svgJar
                                    "chevron-down-gem-50"
                                    height="16"
                                    width="16"
                                    class=(concat "align-middle " (unless notRenderingAllFields "rotate-180" ""))
                                  }}
                                  {{if notRenderingAllFields "Show more" "Show less"}}
                                </UiButton>
                              </td>
                            </tr>
                          {{/if}}
                        {{/let}}
                      {{/let}}
                    </table>
                  {{/each}}
                </Card>
              </div>
            {{else}}
              <Card class="mx-8 my-8" data-test-match-attr="no-data">
                <p class="text-sm !mb-0">
                  Currently, we don't have any further details on this match.
                  {{#if this.isVisualCompliance}}For more information, please visit the
                    <a href="https://www.visualcompliance.com" target="_blank" rel="noopener">
                      Visual Compliance dashboard →
                    </a>
                  {{/if}}
                </p>
              </Card>
            {{/if}}
          </section>

        </div>
        {{#if (gt this.visibleResults.length 1)}}
          <div class="flex items-center justify-end p-4 gap-x-4 border-t border-t-solid border-t-carbon-5">
            <span class="text-sm text-carbon-50 mr-4" data-test-screener-progress>
              {{add this.selectedIndex 1}}
              of
              {{this.visibleResults.length}}
              {{pluralize this.visibleResults.length "screener" withoutCount=true}}
            </span>
            <UiButton
              class="btn btn--small btn--outline [&_path]:fill-carbon-50 flex items-center w-30"
              disabled={{eq this.selectedIndex 0}}
              {{on "click" this.prev}}
              data-test-prev-button
            >
              {{svgJar "chevron-left" height="12" width="12" class="mr-1"}}
              Prev
            </UiButton>

            <UiButton
              class="btn btn--small btn--outline [&_path]:fill-carbon-50 flex items-center w-30"
              disabled={{this.isLast}}
              {{on "click" this.next}}
              data-test-next-button
            >
              Next
              {{svgJar "chevron-left" height="12" width="12" class="rotate-180 ml-1"}}
            </UiButton>
          </div>
        {{/if}}
      </FocusTrapper>
    </ModalDialog>
  </template>
}
